import DieRoll from "@/types/die-roll";
import DieAdjustment from "@/types/die-adjustment";
import DieCode from "@/types/die-code";
import { resolveRange, parseScalar } from "@/types/range-resolver";
import type { SwadeItemDetailInterface, SwadeModConfigurationInterface } from "@/types/items";
import type { AuthoringDeclaration } from "@/appdata/container-fields";
import type { SwadePowerDetailOverridesInterface } from '@/types/powers';
import { foldTextList } from '@/appdata/text-list';

/**
 * Resolves one detail field from a base value plus the modifications targeting it.
 *
 * Dispatch is by FIELD NAME, not by inspecting the value, because the semantics are per-field:
 * `0` means Melee for range, Instant for duration, and none for damage. No value inspection can
 * recover that.
 *
 * An unsupported field returns `undefined`, never `null` — the container-configuration layer treats
 * a null value as "no override" and strips it, so a null result would vanish on persist.
 */
interface ResolutionStrategy extends AuthoringDeclaration {
    resolve(
        base: unknown,
        configurations: SwadeModConfigurationInterface[],
    ): string | number | undefined;
}

function lastAlter(
    configurations: SwadeModConfigurationInterface[],
): SwadeModConfigurationInterface | null {
    const alters = configurations.filter((configuration) => configuration.operator === 'alter');
    return alters.length > 0 ? alters[alters.length - 1] : null;
}

/**
 * The numeric fold, shared by every arithmetic field. Arithmetic runs in fixed stages — alter, then
 * multiply, then additive — but only the multiply and additive stages are independent of the order
 * configurations arrive in: each sorts and reduces its own list rather than folding the raw array.
 * Which `alter` applies is NOT order-independent — it is positional, the last `alter` in the array,
 * by design — and the remaining stages then run against whatever it replaced. `round` is the ONLY
 * difference between the whole-number and fractional variants — the alter/multiply/additive stages
 * and the zero clamp are identical, and duplicating them is what produced this layer's recurring
 * order-dependence bugs. The result always clamps at 0, whatever `round` does with the rest: `0` is
 * a real value here (unarmored, no strain), not an absence.
 */
function resolveNumeric(
    base: unknown,
    configurations: SwadeModConfigurationInterface[],
    round: (value: number) => number,
): number {
    const all = configurations;

    // Every stage parses through the SAME helper the range resolver uses, and a value that does
    // not parse is skipped rather than coerced. `Number(null)`, `Number('')` and `Number(false)`
    // are all 0, so coercing here would let an `alter` or `multiply` with an empty value zero
    // out armor outright.
    const altered = lastAlter(all);
    const replacement = altered ? parseScalar(altered.value) : null;

    let current = replacement ?? parseScalar(base) ?? 0;

    // Sort before reducing. Float arithmetic is commutative but NOT associative, and the
    // terminal floor turns a last-bit difference into an integer one: 9 x 1.2 x 2.5 floors to
    // 26 while 9 x 2.5 x 1.2 floors to 27.
    const canonical = (values: number[]): number[] => [...values].sort((a, b) => a - b);

    // Negative factors are dropped INDIVIDUALLY, before the reduce, exactly as
    // range-resolver.ts does. Filtering the product instead would let a pair of them cancel
    // into a positive and fabricate a value the terminal clamp cannot catch: 3 x -2 x -1 = 6.
    const factors = canonical(
        all.filter((configuration) => configuration.operator === 'multiply')
            .map((configuration) => parseScalar(configuration.value))
            .filter((factor): factor is number => factor !== null && factor >= 0),
    );

    if (factors.length > 0) {
        current = current * factors.reduce((product, factor) => product * factor, 1);
    }

    const deltas = canonical(
        all.filter((configuration) =>
            configuration.operator === 'add' || configuration.operator === 'subtract')
            .map((configuration) => {
                const amount = parseScalar(configuration.value);
                if (amount === null) return null;
                return configuration.operator === 'subtract' ? -amount : amount;
            })
            .filter((delta): delta is number => delta !== null),
    );

    current = current + deltas.reduce((sum, delta) => sum + delta, 0);

    return Math.max(0, round(current));
}

/** Float arithmetic leaves artefacts a persisted value should not carry: 0.1 + 0.2 is
 *  0.30000000000000004. Four decimals erases that while preserving any weight an author could
 *  plausibly enter. */
const toFourDecimals = (value: number): number => Math.round(value * 10000) / 10000;

const numericStrategy: ResolutionStrategy = {
    operators: ['add', 'subtract', 'multiply', 'alter'],
    valueKind: 'integer',
    resolve: (base, configurations) => resolveNumeric(base, configurations, Math.floor),
};

/** Weight is genuinely fractional in SWADE and app-data.ts:235 already sums it with parseFloat, so
 *  flooring here would silently disagree with the total the sheet displays. */
const fractionalStrategy: ResolutionStrategy = {
    operators: ['add', 'subtract', 'multiply', 'alter'],
    valueKind: 'decimal',
    resolve: (base, configurations) => resolveNumeric(base, configurations, toFourDecimals),
};

/**
 * Prose has no arithmetic: the last value wins, but a `block` or an unset operator is ignored —
 * `block` means "prevent" in every other strategy, not "replace with this value", and a null or
 * undefined operator is only ever a blank form template, never an authored instruction — and a
 * modification whose value is null or undefined is skipped so it cannot blank the field.
 *
 * Never returns `undefined` — that is reserved for "this field has no strategy". A null base with
 * nothing applied resolves to the empty string, this strategy's own empty value.
 *
 * AUTHORING DECLARATION IS A DELIBERATE SUBSET. `operators: ['alter']` below offers an author only
 * "set", although resolve() honours add/subtract/multiply identically — prose has no arithmetic, so
 * every non-block operator is a replacement. Offering "add" would read as append and then replace.
 * The equivalence is pinned by string-strategy-operators.test.ts; if resolve() ever distinguishes
 * those operators, that test fails and this declaration must be revisited. `notes` left this
 * strategy on 2026-09-24 for textListStrategy below, where add and subtract do mean something.
 */
const stringStrategy: ResolutionStrategy = {
    operators: ['alter'],
    valueKind: 'text',
    resolve(base, configurations) {
        // `block` means "prevent" in every other strategy, so it is ignored here rather than acting
        // as a replacement. A null/undefined operator is only ever a blank form template (see
        // ChoiceSetConfigurator.vue's default and character-data.ts's coercion to '', which matches
        // no operator branch), so it is ignored the same way rather than treated as a replacement.
        // A null value is skipped for the same reason the numeric stages skip one: a malformed
        // modification must not blank the field.
        const values = configurations.filter(
            (configuration) =>
                configuration.operator !== 'block' &&
                configuration.operator !== null &&
                configuration.operator !== undefined &&
                configuration.value !== null &&
                configuration.value !== undefined,
        );

        const value = values.length === 0 ? base : values[values.length - 1].value;

        return value === null || value === undefined ? '' : String(value);
    },
};

/**
 * A comma-separated text list (owner's ruling 2026-09-24): `alter` overwrites, `add` joins with ", ",
 * `subtract` removes an exact item. The fold is text-list.ts's, the same one the `trapping` policy
 * field runs in rebuildContainerOverrides, so an item's notes and a power's trappings cannot disagree.
 */
const textListStrategy: ResolutionStrategy = {
    operators: ['add', 'subtract', 'alter'],
    valueKind: 'text',
    resolve: (base, configurations) => foldTextList(base, configurations),
};

/**
 * The adjustments one configuration contributes. A single-term value — a die, a number — is one
 * adjustment, exactly as the engine has always taken it. A `modify` value that is a whole formula
 * (`2d6+2`: the owner's damage a blank carried effect takes, container-carry.ts — or an authored one)
 * folds term by term — each dice term and each flat term becomes one adjustment with the term's sign
 * folded into the operator — and an attribute token (`Str`) is the target's own base, never
 * appended. `step` and `multiply` keep the single adjustment: a formula means nothing to a step or a
 * factor, and the engine drops the unparsed value as it always did.
 */
function dieAdjustmentsFor(configuration: SwadeModConfigurationInterface): DieAdjustment[] {
    const single = DieAdjustment.fromConfiguration(configuration);
    if (!single.isParsed() || single.getValueType() !== 'modify' || single.getOperator() === 'multiply') return [single];
    const terms = new DieCode(String(configuration.value)).getTerms();
    if (terms.length === 1 && terms[0].kind !== 'token') return [single];
    const sign = single.getSign();
    return terms.flatMap((term): DieAdjustment[] => {
        if (term.kind === 'token') return [];
        const operator = sign * term.sign < 0 ? 'subtract' : 'add';
        const value = term.kind === 'dice' ? `${term.count}d${term.sides}` : term.value;
        return [DieAdjustment.fromConfiguration({ ...configuration, operator, value, valueType: 'modify' })];
    });
}

/**
 * Wraps layer 1 unchanged. The engine expresses adjustments only — `DieAdjustment.fromConfiguration`
 * rejects `alter` and `DieRoll.add()` drops it — so `alter` is handled here, in front of the engine,
 * the way the numeric fold handles it (owner's ruling 2026-10-18, Shotgun Slugs): the LAST alter
 * with a non-blank value replaces the base formula, then every add/subtract/multiply folds onto it.
 * The other thing added in front of the engine is `dieAdjustmentsFor`: a formula value reaches it as
 * its terms.
 *
 * AUTHORING DECLARATION IS A DELIBERATE SUBSET. `operators` below does not offer `alter`: the
 * editor's value type select (step / modify) means nothing to a replacement. CMS-imported content
 * authors it, and resolve() honours it.
 */
const dieStrategy: ResolutionStrategy = {
    operators: ['add', 'subtract', 'multiply'],
    valueKind: 'integer',
    valueTypes: ['step', 'modify'],
    resolve(base, configurations) {
        const alter = lastAlter(configurations);
        const replacement = alter === null || alter.value === null || alter.value === undefined ? '' : String(alter.value).trim();
        const formula = replacement !== '' ? replacement : base;
        const roll = new DieRoll(formula === null || formula === undefined ? '' : String(formula));
        for (const configuration of configurations) {
            for (const adjustment of dieAdjustmentsFor(configuration)) roll.add(adjustment);
        }
        return roll.getFormula();
    },
};

const rangeStrategy: ResolutionStrategy = {
    operators: ['add', 'subtract', 'multiply', 'alter'],
    valueKind: 'text',
    resolve(base, configurations) {
        const resolved = resolveRange(base, configurations);

        // resolveRange returns undefined for a base its grammar cannot parse. `undefined` is
        // reserved for "this field has no strategy", so hand the base back unchanged instead —
        // the same "ignore what cannot be handled" convention every other malformed input gets.
        if (resolved !== undefined) return resolved;

        // Hand back only a base a sheet can actually render — NaN and Infinity are numbers but not
        // values.
        if (typeof base === 'string') return base;
        return typeof base === 'number' && Number.isFinite(base) && base >= 0 ? base : 0;
    },
};

/**
 * `satisfies` makes the compiler reject a key that is not a real detail field, so this map cannot
 * drift from Swade*DetailInterface the way a hand-maintained list would. It is not exported —
 * `resolveDetail`, `resolvableDetails` and `strategyDeclarationFor` are the whole public surface,
 * and the last two never hand out a strategy.
 *
 * `locations` is deliberately absent: it is a real detail field but an array, which none of the
 * four strategies fits, and no modification targets it.
 */
const STRATEGIES = {
    damage: dieStrategy,
    range: rangeStrategy,

    armor: numericStrategy,
    toughness: numericStrategy,
    ap: numericStrategy,
    parry: numericStrategy,
    cover: numericStrategy,
    weight: fractionalStrategy,
    rateOfFire: numericStrategy,
    payload: numericStrategy,
    strain: numericStrategy,
    installLimit: numericStrategy,

    notes: textListStrategy,
    minStr: stringStrategy,
} satisfies Partial<Record<keyof NonNullable<SwadeItemDetailInterface>, ResolutionStrategy>>;

/** SWADE's five ranks — the `rank` detail's enum. The authoring surface labels them through ux.labels.swade.rank. */
export const POWER_RANKS = ['novice', 'seasoned', 'veteran', 'heroic', 'legendary'] as const;

/**
 * A power's cost (spec 2026-09-11 §3, r3 iii). The numeric fold resolves a null or unparseable base
 * to 0 — right for an unarmored item, wrong for a `Special` or `Varies` cost, whose container keeps
 * `powerPoints` null. So: no result at all unless the base is a number or an alter supplies one;
 * `undefined` means "no strategy result" and the rebuild writes nothing. Multiply is not offered on
 * a cost; a hand-written one still folds, as the string strategy's documented subset does.
 */
const powerPointsStrategy: ResolutionStrategy = {
    operators: ['add', 'subtract', 'alter'],
    valueKind: 'integer',
    resolve(base, configurations) {
        // `lastAlter` returns the positional last alter whatever its value holds; only a PARSEABLE
        // alter supplies a number, so an alter with a blank value counts as no alter here.
        const alter = lastAlter(configurations);
        const altered = alter === null ? null : parseScalar(alter.value);
        if (parseScalar(base) === null && altered === null) return undefined;
        return numericStrategy.resolve(base, configurations);
    },
};

/** `rank`: a last-alter-wins string whose authored values are the five ranks. */
const rankStrategy: ResolutionStrategy = { ...stringStrategy, valueKind: 'enum', values: POWER_RANKS };

/**
 * A power container's stat line. `range`, `damage` and `ap` are the SAME strategy objects as the item
 * map, so the two cannot disagree. Kept apart from STRATEGIES so `resolvableDetails()` keeps meaning
 * item details and the item authoring list does not grow power-only names. Keyed on the override
 * vocabulary (SwadePowerDetailOverridesInterface), so a key that is not a power override is rejected.
 */
const POWER_STRATEGIES = {
    powerPoints: powerPointsStrategy,
    range: rangeStrategy,
    duration: stringStrategy,
    damage: dieStrategy,
    rank: rankStrategy,
    // Armor Piercing (2026-09-23): a whole number over a base no power has, so the fold starts at 0.
    ap: numericStrategy,
} satisfies Partial<Record<keyof SwadePowerDetailOverridesInterface, ResolutionStrategy>>;

/**
 * The lookup `resolveDetail` and `strategyDeclarationFor` use. One union rather than a per-array
 * registry because the rebuild resolves a detail without knowing which array its container sits in;
 * what keeps an item from folding `duration` is the authoring list (`detailsFor`), which is the gate
 * for every path, not this map.
 */
const ALL_STRATEGIES: Record<string, ResolutionStrategy> = { ...STRATEGIES, ...POWER_STRATEGIES };

export function resolveDetail(
    detail: string,
    base: unknown,
    configurations: SwadeModConfigurationInterface[],
): string | number | undefined {
    // Object.hasOwn would be clearer, but it needs the ES2022 lib and this project targets ES2020.
    if (!Object.prototype.hasOwnProperty.call(ALL_STRATEGIES, detail)) return undefined;

    const strategy = ALL_STRATEGIES[detail];

    return strategy.resolve(base, configurations ?? []);
}

/** Every detail the resolver can handle, for the authoring layer to derive its lists from. */
export function resolvableDetails(): string[] {
    return Object.keys(STRATEGIES);
}

/**
 * The authoring declaration of a resolvable detail, or null. Returns a fresh object so `resolve`
 * never leaves this module — `resolveDetail` stays the only way to run a strategy.
 */
export function strategyDeclarationFor(detail: string): AuthoringDeclaration | null {
    if (!Object.prototype.hasOwnProperty.call(ALL_STRATEGIES, detail)) return null;
    const { operators, valueKind, values, valueTypes } = ALL_STRATEGIES[detail];
    return { operators, valueKind, ...(values ? { values } : {}), ...(valueTypes ? { valueTypes } : {}) };
}
